import * as React from 'react';
import { encodeSvg } from '../../icons/Icons';
import { makeTestId } from '../../utils/Declarative';
import { isElementOverflownX } from '../../utils/DomUtil';
import { TabContent } from './TabsWidget';
import { DefaultTabLabelStyleName, LabelStyle, TabContentStyle, TabStyleName } from './TabsWidgetStyles';

export interface TabProps {
  ariaControls: string;
  id: string;
  index: number;
  isActive: boolean;
  isFocused: boolean;

  /**
   * Optional value indicating the amount of flex (as a percentage) for this tab.
   * This value will be defined when the chosen TabsAlignment is 'justified'.
   */
  flexPercent?: number;

  onBlur: () => void;
  onClick: (index: number) => void;
  onFocus: (index: number) => void;
  onKeyDown: (index: number, keyboardEvent: React.KeyboardEvent<HTMLDivElement>) => void;
  tabContent: TabContent;
  tabContainerStyles: React.CSSProperties;
}

export interface TabState {
  title?: string;
}

/**
 * TabWidget is a component that renders a tab, stylized based on whether it's active, hovered, or pressed
 */
export class TabWidget extends React.PureComponent<TabProps, TabState> {
  public state: TabState = {};
  private labelRef: HTMLDivElement | null = null;
  private tabRef: HTMLDivElement | null = null;

  public componentDidUpdate(prevProps: TabProps): void {
    if (this.props.isFocused && !prevProps.isFocused && this.tabRef) {
      this.tabRef.focus();
    }
  }

  private handleTabRef = (tabRef: HTMLDivElement | null): void => {
    this.tabRef = tabRef;
  }

  private handleLabelRef = (labelRef: HTMLDivElement | null): void => {
    this.labelRef = labelRef;
  }

  private handleMouseEnter = (): void => {
    const title = (!!this.labelRef && isElementOverflownX(this.labelRef)) ? this.props.tabContent.label : undefined;
    this.setState({ title });
  }

  private handleClick = (): void => {
    this.props.onClick(this.props.index);
  }

  private handleKeyDown = (keyboardEvent: React.KeyboardEvent<HTMLDivElement>): void => {
    this.props.onKeyDown(this.props.index, keyboardEvent);
  }

  private handleFocus = (): void => {
    this.props.onFocus(this.props.index);
  }

  public render(): JSX.Element {
    return (
      <div
        aria-controls={this.props.ariaControls}
        aria-label={this.props.tabContent.label}
        aria-selected={this.props.isActive}
        className={TabStyleName}
        id={this.props.id}
        onBlur={this.props.onBlur}
        onClick={this.handleClick}
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
        onMouseEnter={this.handleMouseEnter}
        onTouchEnd={this.handleClick}
        ref={this.handleTabRef}
        role='tab'
        style={{ ...(!!this.props.flexPercent && { flex: `1 0 ${this.props.flexPercent}%` }) }}
        tabIndex={this.props.isActive || this.props.isFocused ? 0 : -1}
        title={this.props.tabContent.title || this.state.title}
      >
        <div style={this.props.tabContainerStyles}>
          <div className={DefaultTabLabelStyleName}>
            {this.renderIcon()}
            {this.renderLabel()}
          </div>
        </div>
      </div>
    );
  }

  private renderIcon(): React.ReactNode {
    const tabIcon = this.props.tabContent.icon;
    if (!tabIcon) { return undefined; }

    const iconProps = {
      'data-tb-test-id': makeTestId(this.props.id, 'icon'),
      style: TabContentStyle
    };

    const element = (typeof tabIcon === 'string') ? <img src={encodeSvg(tabIcon as string)} /> : tabIcon;
    return <div {...iconProps}>{element}</div>;
  }

  private renderLabel(): JSX.Element {
    const labelProps = {
      'data-tb-test-id': makeTestId(this.props.id, 'label'),
      ref: this.handleLabelRef,
      style: LabelStyle
    };

    return <div style={TabContentStyle}>
      <div {...labelProps}>{this.props.tabContent.label}</div>
    </div>;
  }
}
